/*
  getProductBadges:
  This function is designed to generate an array of badge objects for a product based on its tags. It supports custom badges as well as predefined badge types like 'bestseller' and 'customizable'. The function is designed to handle a single product and applies a limit to the number of badges returned.

  - Parameters:
    - product: The product object from which to derive badges.

  - Returns:
    - An array containing a single badge object based on predefined priorities, unless no applicable badges are found, in which case it returns an empty array.

  - Implementation Details:
    - The function first checks if the product is defined. If not, it returns an empty array.
    - It then processes the product's tags to identify applicable badges.
    - 'customBadge' dynamically creates badges based on tags starting with 'productbadge:', capitalizing the first letter of the badge text.
    - 'bestseller' and 'customizable' are straightforward checks against the product's tags.
    - Badges are prioritized based on a predefined list and limited to one badge per product to maintain simplicity and focus in the UI.

  This function is intended to be easily extendable for additional badge types and can be integrated into product listing or detail views to enhance product information dynamically.
*/

const getProductBadges = (product) => {
  if (!product) return [];

  const BADGE_LIMIT = 1;
  const tags = product?.tags?.map((tag) => tag.toLowerCase());

  const getBadge = {

    customBadge: () => {
      const customTag = tags.find(tag => tag.startsWith('productbadge:'));
      if (customTag) {
        const label = customTag.slice(13);
        return {
          label: label.charAt(0).toUpperCase() + label.slice(1),
        };
      }
    },

    exclusive: () =>
      tags.includes('exclusive') && {
        label: 'Exclusive',
      },

    bestseller: () =>
      tags.includes('bestseller') && {
        label: 'Bestseller',
      },

    customizable: () =>
      tags.includes('customizable') && {
        label: 'Customizable',
      },
  };

  const badgePriority = [
    'customBadge',
    'exclusive',
    'bestseller',
    'customizable',
  ];

  const allBadges = badgePriority
    .map((badge) => {
      const badgeData = typeof getBadge[badge] === 'function' && getBadge[badge]();
      if (badgeData) {
        badgeData.type = badge;
        return badgeData;
      }
    })
    .filter((badge) => !!badge);

  return allBadges.slice(0, BADGE_LIMIT);
};
